const LOCATION_STATUS_KEY = "looksty_client_location_status";

const LOCATION_LAT_KEY = "looksty_client_latitude";

const LOCATION_LNG_KEY = "looksty_client_longitude";

const LOCATION_UPDATED_AT_KEY = "looksty_client_location_updated_at";

export type LocationRequestError = "unsupported" | "not-secure" | "denied" | "unavailable" | "timeout" | "unknown";

export interface LocationRequestResult {
  success: boolean;
  error?: LocationRequestError;
  message?: string;
}

export type LocationStatus = "granted" | "denied";

export class ClientLocationService {
  public static getStatus(): LocationStatus | "" {
    if (typeof window === "undefined") {
      return "";
    }

    return (window.localStorage.getItem(LOCATION_STATUS_KEY) as LocationStatus | null) ?? "";
  }

  public static getLatitude(): string {
    if (typeof window === "undefined") {
      return "";
    }

    return window.localStorage.getItem(LOCATION_LAT_KEY) ?? "";
  }

  public static getLongitude(): string {
    if (typeof window === "undefined") {
      return "";
    }

    return window.localStorage.getItem(LOCATION_LNG_KEY) ?? "";
  }

  public static getUpdatedAt(): number | null {
    if (typeof window === "undefined") {
      return null;
    }

    const value = Number(window.localStorage.getItem(LOCATION_UPDATED_AT_KEY));

    return Number.isFinite(value) ? value : null;
  }

  public static hasUsableLocation(): boolean {
    const headers = this.getHeaders();

    return Boolean(headers["X-Client-Latitude"] && headers["X-Client-Longitude"]);
  }

  public static getHeaders(): Record<string, string> {
    if (this.getStatus() !== "granted") {
      return {};
    }

    const latitude = this.getLatitude().trim();
    const longitude = this.getLongitude().trim();

    if (!latitude || !longitude) {
      return {};
    }

    const latNumber = Number(latitude);
    const lngNumber = Number(longitude);

    if (!Number.isFinite(latNumber) || latNumber < -90 || latNumber > 90) {
      this.clear();

      return {};
    }

    if (!Number.isFinite(lngNumber) || lngNumber < -180 || lngNumber > 180) {
      this.clear();

      return {};
    }

    return {
      "X-Client-Latitude": latitude,
      "X-Client-Longitude": longitude,
    };
  }

  public static async requestLocation(): Promise<LocationRequestResult> {
    if (typeof window === "undefined") {
      return {
        success: false,
        error: "unsupported",
      };
    }

    if (!window.isSecureContext) {
      return {
        success: false,
        error: "not-secure",
        message: "Geolocation requires a secure context",
      };
    }

    if (!("geolocation" in navigator)) {
      return {
        success: false,
        error: "unsupported",
      };
    }

    return new Promise<LocationRequestResult>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latitude = this.normalizeCoordinate(position.coords.latitude);

          const longitude = this.normalizeCoordinate(position.coords.longitude);

          window.localStorage.setItem(LOCATION_LAT_KEY, latitude);

          window.localStorage.setItem(LOCATION_LNG_KEY, longitude);

          window.localStorage.setItem(LOCATION_UPDATED_AT_KEY, String(Date.now()));

          window.localStorage.setItem(LOCATION_STATUS_KEY, "granted");

          resolve({
            success: true,
          });
        },
        (error) => {
          this.clearCoordinates();

          console.error("Geolocation failed", {
            code: error.code,
            message: error.message,
            secureContext: window.isSecureContext,
          });

          switch (error.code) {
            case error.PERMISSION_DENIED:
              window.localStorage.setItem(LOCATION_STATUS_KEY, "denied");

              resolve({
                success: false,
                error: "denied",
                message: error.message,
              });

              return;

            case error.POSITION_UNAVAILABLE:
              window.localStorage.removeItem(LOCATION_STATUS_KEY);

              resolve({
                success: false,
                error: "unavailable",
                message: error.message,
              });

              return;

            case error.TIMEOUT:
              window.localStorage.removeItem(LOCATION_STATUS_KEY);

              resolve({
                success: false,
                error: "timeout",
                message: error.message,
              });

              return;

            default:
              window.localStorage.removeItem(LOCATION_STATUS_KEY);

              resolve({
                success: false,
                error: "unknown",
                message: error.message,
              });
          }
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 1000 * 60 * 30,
        }
      );
    });
  }

  public static clear(): void {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.removeItem(LOCATION_STATUS_KEY);

    this.clearCoordinates();
  }

  private static clearCoordinates(): void {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.removeItem(LOCATION_LAT_KEY);

    window.localStorage.removeItem(LOCATION_LNG_KEY);

    window.localStorage.removeItem(LOCATION_UPDATED_AT_KEY);
  }

  private static normalizeCoordinate(value: number): string {
    return String(Number(value.toFixed(6)));
  }
}
